import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  ChevronDown, ChevronUp, LogOut, HelpCircle,
  Settings, User, Bell, Search, ChevronLeft, ChevronRight,
  SearchX, LayoutGrid
} from 'lucide-react';
import logo from '../assets/images/logo.png';
import '../css/sidebar.css'
import { FRONTEND_URL } from '../config/axios';
import { toast } from 'react-toastify';

const SidebarLink = ({ data, currentPath, isExpanded }) => {
  currentPath = currentPath.split("/");
  let paths = currentPath[2];
  const active = data.userId === paths;

  return (
    <Link
      to={`${data.userId}`}
      className={`modern-sidebar-link ${active ? 'active' : ''} ${!isExpanded ? 'collapsed' : ''}`}
      title={!isExpanded ? data.name : ''}
    >
      <div className="link-icon" dangerouslySetInnerHTML={{ __html: active ? data.svgfill : data.svg }} />
      {isExpanded && <span className="link-label">{data.name}</span>}
    </Link>
  );
};

const SidebarDropDown = ({ data, currentPath, isExpanded }) => {
  currentPath = currentPath.split("/");
  let paths = currentPath[2];
  const [isOpen, setIsOpen] = useState(false);
  const hasActiveChild = data.subMenu?.some(item => item.userId === paths);

  useEffect(() => {
    if (hasActiveChild && isExpanded) {
      setIsOpen(true);
    }
  }, [hasActiveChild, isExpanded]);

  const toggleDropdown = () => {
    if (isExpanded) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={`modern-dropdown ${isOpen ? 'open' : ''}`}>
      <button
        className={`modern-dropdown-btn ${hasActiveChild ? 'has-active' : ''} ${!isExpanded ? 'collapsed' : ''}`}
        onClick={toggleDropdown}
        title={!isExpanded ? data.name : ''}
      >
        <div className="dropdown-icon" dangerouslySetInnerHTML={{ __html: hasActiveChild ? data.svgfill : data.svg }} />
        {isExpanded && (
          <>
            <span className="dropdown-label">{data.name}</span>
            <div className="dropdown-arrow">
              {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </>
        )}
      </button>

      {isExpanded && (
        <div className={`modern-dropdown-menu ${isOpen ? 'expanded' : ''}`}>
          {data.subMenu?.map((subItem, idx) => {
            const isSubActive = subItem.userId === paths;
            return (
              <Link
                key={idx}
                to={`${subItem.userId}`}
                className={`modern-submenu-link ${isSubActive ? 'active' : ''}`}
              >
                <div
                  className="submenu-icon"
                  dangerouslySetInnerHTML={{ __html: isSubActive ? subItem.svgfill : subItem.svg }}
                />
                <span>{subItem.name}</span>
                {isSubActive && <div className="sub-active-dot" />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DashboardSidebar = ({ sidebarData, isExpanded, setIsExpanded, isHovered, setIsHovered }) => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState(sidebarData);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const location = useLocation();

  const userName = sessionStorage.getItem("userName") || sessionStorage.getItem("username") || "User";
  const empname = sessionStorage.getItem("name");
  const empCode = sessionStorage.getItem("emp_code");
  const role = sessionStorage.getItem("role");

  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, [location]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768 && isExpanded) {
        setIsExpanded(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isExpanded]);

  const handleLogout = () => {
    toast.info("Logging Out...");
    sessionStorage.clear();
    setTimeout(() => {
      window.location = FRONTEND_URL;
    }, 500);
  };

  useEffect(() => {
    if (searchTerm) {
      const filtered = sidebarData.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.subMenu?.some(sub => sub.name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(sidebarData);
    }
  }, [searchTerm, sidebarData]);

  const getFirstLetter = () => {
    if (empname) {
      return empname.charAt(0).toUpperCase();
    } else if (empCode) {
      return empCode.charAt(0).toUpperCase();
    } else {
      return "U";
    }
  };

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
    setIsHovered(false);
  };

  const handleMouseEnter = () => {
    if (!isExpanded && !isMobile) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const sidebarWidth = isExpanded || (isHovered && !isExpanded) ? '260px' : '72px';
  const isSearching = searchTerm.trim().length > 0;

  return (
    <>
      {/* Mobile Overlay */}
      {isExpanded && isMobile && (
        <div className="sidebar-overlay" onClick={() => setIsExpanded(false)} />
      )}

      <div
        className={`modern-sidebar-fixed ${isExpanded ? 'expanded' : 'collapsed'} ${isHovered && !isExpanded ? 'hover-expanded' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ width: sidebarWidth }}
      >
        {/* Sidebar Header */}
        <div className="sidebar-header">
          <div className="logo-container">
            {(isExpanded || isHovered) ? (
              <>
                <img src={logo} alt="CIPL Logo" className="sidebar-logo" />
                <div className="logo-text">
                  <span className="logo-title">LTS Portal</span>
                </div>
              </>
            ) : (
              <img src={logo} alt="CIPL Logo" className="sidebar-logo-collapsed" />
            )}
          </div>
          {(isExpanded || isHovered) &&
            <button className="sidebar-toggle-btn" onClick={toggleSidebar}>
              {isExpanded ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
            </button>
          }
        </div>

        {/* Search Bar - Only show when expanded */}
        {(isExpanded || isHovered) && (
          <div className="sidebar-search">
            <div className="search-container">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search menu..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Navigation Menu */}
        <nav className="sidebar-nav">
          <div className={`nav-items ${filteredData.length === 0 ? 'empty-state' : ''}`}>
            {filteredData.length > 0 ? (
              filteredData.map((data, index) => {
                return data.subMenu && data.subMenu.length > 0 ? (
                  <SidebarDropDown
                    key={index}
                    data={data}
                    currentPath={currentPath}
                    isExpanded={isExpanded || isHovered}
                  />
                ) : (
                  <SidebarLink
                    key={index}
                    data={data}
                    currentPath={currentPath}
                    isExpanded={isExpanded || isHovered}
                  />
                );
              })
            ) : (
              <div className="no-results">
                <div className="no-results-icon">
                  {isSearching ? <SearchX size={22} /> : <LayoutGrid size={22} />}
                </div>
                {(isExpanded || isHovered) && (
                  <>
                    <span className="no-results-title">
                      {isSearching ? 'No matches found' : 'No menu items'}
                    </span>
                    <span className="no-results-hint">
                      {isSearching ? 'Try a different keyword' : 'Nothing assigned to this role yet'}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          <div className="footer-divider" />

          <div className="user-section">
            <div className="user-avatar" style={{ borderRadius: (!isHovered && !isExpanded) && "35px", width: (!isHovered && !isExpanded) && "40px", height: (!isHovered && !isExpanded) && "40px" }}>
              <span>{getFirstLetter()}</span>
            </div>
            {(isExpanded || isHovered) && (
              <div className="user-info">
                <span className="user-name">{empname ? empname : empCode ? empCode : "User"}</span>
                <span className="user-role">{role ? role : "User"}</span>
              </div>
            )}

          </div>

          <button className="logout-btn" style={{ width: (!isExpanded && !isHovered) && "100%" }} onClick={handleLogout}>
            <LogOut size={18} />
            {(isExpanded || isHovered) && <span>Logout</span>}
          </button>
        </div>
      </div>
    </>
  );
};

export default DashboardSidebar;